import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Heart, ShieldCheck } from 'lucide-react'
import { Section, SectionHeading, IconCircle, CheckItem, FAQ, CTABand, SplitHero, NonMedicalNote } from '../components/ui'
import { PHONE_DISPLAY, PHONE_TEL } from '../lib/constants'
import { submitApplication } from '../lib/api'

const whyStay = [
  'Consistent clients and schedules that respect your life',
  'Written care plans so you always know the expectations',
  'A coordinator who picks up the phone',
  'Recognition for the relationship you build, not just tasks completed',
]

const responsibilities = [
  "Follow each client's written care plan and task checklist",
  'Respectful personal care support such as bathing, dressing and grooming',
  'Meal preparation and light housekeeping',
  'Companionship, conversation and encouragement to stay active',
  'Gentle reminders for routines and daily activities',
  'Write a clear shift note after every visit',
  'Tell the coordinator right away when something changes',
]

const screening = [
  "Vulnerable Sector Check (we'll guide you through it)",
  'Two verifiable work or care references',
  'Copies of any certifications (HCA, PSW, First Aid, CPR)',
  'In-person or video interview with a care coordinator',
  'Valid work eligibility in Canada',
  'Comfortable communicating in written shift notes',
]

const faqs = [
  { q: 'Do I need medical training to work here?', a: 'No. True North provides non-medical in-home care, so we hire for warmth, reliability and practical skills. Certifications are welcome but not required for every role, and medical tasks always stay with healthcare professionals.' },
  { q: 'How are shifts scheduled?', a: "You tell us the days and times you're available and we match you with clients nearby. Shifts appear in your caregiver portal, where you check in, work through the task checklist and write your shift note." },
]

const availabilityOpts = ['Weekday mornings', 'Weekday afternoons', 'Weekday evenings', 'Weekends', 'Overnight']
const transportOpts = ['Yes — I have a vehicle and licence', 'Yes — transit or rideshare', 'Not at the moment']
const vscOpts = ['I have a current Vulnerable Sector Check', "I have applied and I'm waiting", "Not yet — I'm willing to complete one"]

const labelHint = { fontWeight: 400, color: 'var(--color-muted)' }

const RESUME_MAX_BYTES = 4 * 1024 * 1024

/** Strips the "data:...;base64," prefix so the server receives raw base64. */
const fileToBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(String(reader.result).split(',')[1] || '')
    reader.onerror = () => reject(new Error('That file could not be read. Please try attaching it again.'))
    reader.readAsDataURL(file)
  })

function ApplicationForm() {
  const [f, setF] = useState({
    fullName: '', email: '', phone: '', city: '',
    transport: transportOpts[0], vsc: vscOpts[0],
    certs: '', experience: '', message: '', consent: false,
  })
  const [availability, setAvailability] = useState([])
  const [resume, setResume] = useState(null)
  const [errors, setErrors] = useState({})
  const [sent, setSent] = useState(false)
  const [refId, setRefId] = useState('')
  const [busy, setBusy] = useState(false)

  const onField = (e) => {
    setF({ ...f, [e.target.name]: e.target.type === 'checkbox' ? e.target.checked : e.target.value })
    setErrors({})
  }
  const toggleAvail = (label) => {
    setAvailability(availability.includes(label) ? availability.filter((x) => x !== label) : [...availability, label])
    setErrors({})
  }

  const submit = async () => {
    if (busy) return
    const errs = {}
    if (!f.fullName.trim()) errs.fullName = 'Please enter your full name.'
    if (!f.email.trim()) errs.email = 'Please enter your email.'
    if (!f.phone.trim()) errs.phone = 'Please enter your phone number.'
    if (!f.city.trim()) errs.city = 'Please enter your city or community.'
    if (!resume) errs.resume = 'Please attach your resume.'
    else if (resume.size > RESUME_MAX_BYTES) errs.resume = 'Please attach a resume smaller than 4 MB.'
    if (!f.consent) errs.consent = 'Please confirm the consent checkbox so we can review your application.'
    setErrors(errs)
    if (Object.keys(errs).length) return
    setBusy(true)
    try {
      const dataBase64 = await fileToBase64(resume)
      const { reference } = await submitApplication({
        resume: {
          filename: resume.name,
          mime: resume.type || 'application/octet-stream',
          dataBase64,
        },
        fullName: f.fullName.trim(),
        email: f.email,
        phone: f.phone,
        city: f.city,
        availability: availability.join(', '),
        transport: f.transport,
        vscStatus: f.vsc,
        certifications: f.certs,
        experience: f.experience,
        message: f.message,
        consent: true,
      })
      setRefId(reference)
      setSent(true)
    } catch (err) {
      setErrors({ submit: err.message })
    } finally {
      setBusy(false)
    }
  }

  if (sent) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
        <h3 className="h2-section">Thank you for applying.</h3>
        <p style={{ margin: 0, fontSize: 19, lineHeight: 1.7, color: 'var(--color-muted)' }}>
          Our care team reviews every application personally. If your experience fits an opening, we'll reach out to arrange an interview.
        </p>
        <p style={{ margin: 0, fontSize: 17, lineHeight: 1.6, color: 'var(--color-muted)', overflowWrap: 'anywhere' }}>
          Your reference number: <strong style={{ color: 'var(--color-teal-900)' }}>{refId}</strong>
        </p>
        <Link to="/about" className="btn btn-secondary" style={{ alignSelf: 'flex-start' }}>Learn about our care standards</Link>
      </div>
    )
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      <div>
        <label className="field-label" htmlFor="app-fullName">Full name *</label>
        <input id="app-fullName" className="field-input" name="fullName" value={f.fullName} onChange={onField} />
        {errors.fullName && <p className="field-error">{errors.fullName}</p>}
      </div>
      <div className="grid grid-2" style={{ gap: 24 }}>
        <div style={{ minWidth: 0 }}>
          <label className="field-label" htmlFor="app-email">Email *</label>
          <input id="app-email" className="field-input" name="email" type="email" value={f.email} onChange={onField} />
          {errors.email && <p className="field-error">{errors.email}</p>}
        </div>
        <div style={{ minWidth: 0 }}>
          <label className="field-label" htmlFor="app-phone">Phone *</label>
          <input id="app-phone" className="field-input" name="phone" type="tel" value={f.phone} onChange={onField} />
          {errors.phone && <p className="field-error">{errors.phone}</p>}
        </div>
      </div>
      <div className="grid grid-2" style={{ gap: 24 }}>
        <div style={{ minWidth: 0 }}>
          <label className="field-label" htmlFor="app-city">City or community *</label>
          <input id="app-city" className="field-input" name="city" value={f.city} onChange={onField} />
          {errors.city && <p className="field-error">{errors.city}</p>}
        </div>
        <div style={{ minWidth: 0 }}>
          <label className="field-label" htmlFor="app-transport">Do you have reliable transportation?</label>
          <select id="app-transport" className="field-input" name="transport" value={f.transport} onChange={onField}>
            {transportOpts.map((o) => <option key={o}>{o}</option>)}
          </select>
        </div>
      </div>
      <div>
        <label className="field-label">Availability <span style={labelHint}>— choose every block that works for you</span></label>
        <div className="grid grid-2" style={{ gap: 12 }}>
          {availabilityOpts.map((label) => {
            const sel = availability.includes(label)
            return (
              <button
                key={label}
                type="button"
                onClick={() => toggleAvail(label)}
                aria-pressed={sel}
                style={{
                  display: 'flex', minHeight: 52, alignItems: 'center', gap: 12, borderRadius: 4,
                  border: '1px solid var(--color-line)', background: 'var(--color-panel)', padding: '12px 16px',
                  width: '100%', minWidth: 0, boxSizing: 'border-box',
                  fontSize: 16, fontFamily: 'var(--font-sans)', cursor: 'pointer', textAlign: 'left', color: 'var(--color-ink)',
                }}
              >
                <span style={{
                  display: 'flex', height: 24, width: 24, flexShrink: 0, alignItems: 'center', justifyContent: 'center',
                  borderRadius: 4, border: '2px solid var(--color-teal-800)',
                  background: sel ? 'var(--color-teal-800)' : '#FFFFFF',
                  color: 'var(--color-cream)', fontSize: 14, fontWeight: 700,
                }}>{sel ? '✓' : ''}</span>
                {label}
              </button>
            )
          })}
        </div>
      </div>
      <div>
        <label className="field-label" htmlFor="app-vsc">Vulnerable Sector Check status</label>
        <select id="app-vsc" className="field-input" name="vsc" value={f.vsc} onChange={onField}>
          {vscOpts.map((o) => <option key={o}>{o}</option>)}
        </select>
      </div>
      <div>
        <label className="field-label" htmlFor="app-certs">Certifications <span style={labelHint}>— for example HCA, PSW, First Aid, CPR — or none yet</span></label>
        <input id="app-certs" className="field-input" name="certs" value={f.certs} onChange={onField} />
      </div>
      <div>
        <label className="field-label" htmlFor="app-experience">Care experience</label>
        <textarea
          id="app-experience" className="field-input" name="experience" style={{ minHeight: 110 }}
          placeholder="Paid or family caregiving, years of experience, the kind of support you enjoy most."
          value={f.experience} onChange={onField}
        />
      </div>
      <div>
        <label className="field-label" htmlFor="app-message">Anything else you'd like us to know?</label>
        <textarea id="app-message" className="field-input" name="message" style={{ minHeight: 110 }} value={f.message} onChange={onField} />
      </div>
      <div style={{ minWidth: 0 }}>
        <label className="field-label" htmlFor="app-resume">Resume* <span style={labelHint}>— PDF or Word document, up to 4 MB</span></label>
        <input
          id="app-resume" className="field-input" type="file" accept=".pdf,.doc,.docx,.rtf,.txt"
          style={{ maxWidth: '100%', height: 'auto', padding: '12px', fontSize: 16 }}
          onChange={(e) => {
            setResume(e.target.files?.[0] || null)
            setErrors({})
          }}
        />
        {resume && (
          <p style={{ margin: '8px 0 0 0', fontSize: 15, color: 'var(--color-muted)', overflowWrap: 'anywhere' }}>
            Attached: <strong style={{ color: 'var(--color-teal-900)' }}>{resume.name}</strong> ({Math.max(1, Math.round(resume.size / 1024))} KB)
          </p>
        )}
        {errors.resume && <p className="field-error">{errors.resume}</p>}
      </div>
      <div>
        <label style={{ display: 'flex', alignItems: 'flex-start', gap: 12, fontSize: 16, color: 'var(--color-muted)', cursor: 'pointer' }}>
          <input
            type="checkbox" name="consent" checked={f.consent} onChange={onField}
            style={{ width: 24, height: 24, minHeight: 24, marginTop: 2, flexShrink: 0 }}
          />
          I confirm my information is accurate and consent to True North Care &amp; Support reviewing my application and contacting my references.
        </label>
        {errors.consent && <p className="field-error">{errors.consent}</p>}
      </div>
      {errors.submit && <p className="field-error">{errors.submit}</p>}
      <button type="button" onClick={submit} disabled={busy} className="btn btn-primary" style={{ alignSelf: 'flex-start' }}>
        {busy ? 'Submitting…' : 'Submit caregiver application'}
      </button>
    </div>
  )
}

export default function Careers() {
  return (
    <>
      <SplitHero
        eyebrow="Careers"
        title="Caregiving work where you're known, supported, and never rushed."
        lede="We're a Canadian-owned Calgary team hiring caregivers who lead with patience. You'll have written care plans, a coordinator who answers, and clients you get to know well."
        image="/assets/careers.jpg"
        imageAlt="A caregiver smiling with a senior client in a bright living room"
        actions={
          <>
            <a href="#apply" className="btn btn-primary">Apply to be a caregiver</a>
            <a href={PHONE_TEL} className="btn btn-secondary">Call {PHONE_DISPLAY}</a>
          </>
        }
      />

      {/* Why stay + what we look for */}
      <Section tone="cream">
        <div className="grid grid-2">
          <div className="card-teal">
            <IconCircle icon={Heart} variant="on-teal" />
            <h3 style={{ margin: '20px 0 0 0', fontSize: '1.5rem', lineHeight: 1.2, color: 'var(--color-cream)' }}>Why caregivers stay with True North</h3>
            <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 12, fontSize: 17, lineHeight: 1.6, color: 'oklch(0.9765 0.0098 87.47 / 90%)' }}>
              {whyStay.map((w) => <span key={w}>{w}</span>)}
            </div>
          </div>
          <div className="card-cream">
            <IconCircle icon={ShieldCheck} />
            <h3 style={{ margin: '20px 0 0 0', fontSize: '1.5rem', lineHeight: 1.2, color: 'var(--color-teal-900)' }}>What we look for</h3>
            <p style={{ margin: '12px 0 0 0', fontSize: 17, lineHeight: 1.6, color: 'var(--color-muted)' }}>
              Kindness first. Then reliability, clear communication, and comfort with the practical parts of home life — cooking, tidying, driving and good notes.
            </p>
            <p style={{ margin: '16px 0 0 0', fontSize: 17, lineHeight: 1.6, color: 'var(--color-muted)' }}>
              Our care is non-medical, so you'll never be asked to perform nursing tasks.
            </p>
          </div>
        </div>
      </Section>

      {/* The role */}
      <Section tone="panel">
        <SectionHeading
          eyebrow="The role"
          title="What a caregiver does with us"
          lede="Every visit follows a plan built with the family, so the day is clear before you arrive."
        />
        <div className="grid grid-2" style={{ marginTop: 40, gap: 16, maxWidth: 920, width: '100%' }}>
          {responsibilities.map((r) => <CheckItem key={r}>{r}</CheckItem>)}
        </div>
      </Section>

      {/* Screening */}
      <Section tone="cream">
        <div className="grid grid-2" style={{ gap: 48, alignItems: 'start' }}>
          <div style={{ minWidth: 0 }}>
            <SectionHeading
              eyebrow="Screening"
              title="How we keep families safe"
              lede="Every caregiver completes the same screening steps before their first shift."
            />
            <div style={{ marginTop: 32, display: 'flex', flexDirection: 'column', gap: 16 }}>
              {screening.map((s) => <CheckItem key={s}>{s}</CheckItem>)}
            </div>
          </div>
          <NonMedicalNote />
        </div>
      </Section>

      {/* Application */}
      <Section tone="panel-deep" id="apply">
        <SectionHeading
          eyebrow="Apply"
          title="Caregiver application"
          lede="Tell us about yourself. If a form isn't convenient, call us and we'll take your details over the phone."
        />
        <div style={{ marginTop: 40, maxWidth: 768, width: '100%', boxSizing: 'border-box', borderRadius: 8, border: '1px solid var(--color-line)', background: '#FFFFFF', padding: 'clamp(20px, 5vw, 40px)' }}>
          <ApplicationForm />
        </div>
      </Section>

      {/* FAQ */}
      <Section tone="cream">
        <FAQ eyebrow="Common questions" title="Questions caregivers ask" items={faqs} />
      </Section>

      <CTABand
        title="Know someone who'd be a wonderful caregiver?"
        lede="Share this page, or call our office and we'll walk them through the application."
        primaryLabel="Apply to be a caregiver"
        primaryTo="#apply"
      />
    </>
  )
}
